import { VectorTile } from '@mapbox/vector-tile';
import { PbfReader } from 'pbf';
import { config } from '../config';
import { bboxAround, isValidLatLng, lngLatToTile, randomPointNear, type LatLng } from '../game/geo';
import { pickPlace, type GameMap } from '../game/maps';
import { prefersLightImages } from './device';
import {
  HttpError,
  allAttemptsFailed,
  batchSearch,
  describeError,
  fetchArrayBuffer,
  fetchJson,
  isFarEnough,
  nearestPanorama,
} from './search';
import {
  NoPanoramaFoundError,
  ProviderUnavailableError,
  type FindOptions,
  type NeighborLink,
  type Panorama,
  type PanoramaProvider,
} from './types';

/**
 * Mapillary (https://www.mapillary.com): worldwide crowdsourced street-level imagery, CC-BY-SA 4.0.
 * Needs a free client access token (VITE_MAPILLARY_ACCESS_TOKEN).
 *
 * Random panoramas are found with the coverage vector tiles (layer "image", zoom 14,
 * property `is_pano`), as recommended by Mapillary for spatial queries; details come
 * from the Graph API v4.
 */

const GRAPH_URL = 'https://graph.mapillary.com';
const TILE_URL = 'https://tiles.mapillary.com/maps/vtp/mly1_public/2';
const TILE_ZOOM = 14;
const JITTER_KM = 6;
const MAX_ORIGINAL_WIDTH = 8192;

const BASE_FIELDS = [
  'id',
  'geometry',
  'computed_geometry',
  'camera_type',
  'compass_angle',
  'computed_compass_angle',
  'captured_at',
  'creator',
  'sequence',
  'width',
  'height',
  'thumb_1024_url',
  'thumb_2048_url',
];

export interface MapillaryImage {
  id?: string;
  geometry?: { coordinates?: [number, number] };
  computed_geometry?: { coordinates?: [number, number] };
  camera_type?: string;
  compass_angle?: number;
  computed_compass_angle?: number;
  captured_at?: number | string;
  creator?: { username?: string; id?: string };
  sequence?: string;
  width?: number;
  height?: number;
  thumb_1024_url?: string;
  thumb_2048_url?: string;
  thumb_original_url?: string;
}

interface MapillaryMeta {
  sequence?: string;
}

interface TileImage {
  id: number | string;
  sequenceId?: string;
  position: LatLng;
}

const SPHERICAL_TYPES = new Set(['spherical', 'equirectangular']);

function toLatLng(coords: unknown): LatLng | null {
  if (!Array.isArray(coords) || coords.length < 2) return null;
  const p = { lat: Number(coords[1]), lng: Number(coords[0]) };
  return isValidLatLng(p) ? p : null;
}

/** Converts a Graph API image into a playable panorama, or `null` if it is not a full 360° picture. */
export function parseMapillaryImage(image: MapillaryImage, light = false): Panorama | null {
  if (!image?.id || !SPHERICAL_TYPES.has(String(image.camera_type))) return null;
  const position = toLatLng(image.computed_geometry?.coordinates) ?? toLatLng(image.geometry?.coordinates);
  if (!position) return null;
  if (image.width && image.height && Math.abs(image.width / image.height - 2) > 0.05) return null;

  const original =
    !light && image.thumb_original_url && (!image.width || image.width <= MAX_ORIGINAL_WIDTH) ? image.thumb_original_url : undefined;
  const urls = (light ? [image.thumb_2048_url, image.thumb_1024_url] : [original, image.thumb_2048_url, image.thumb_1024_url]).filter(
    (u): u is string => typeof u === 'string' && u.length > 0,
  );
  if (urls.length === 0) return null;

  const capturedAt =
    image.captured_at !== undefined && !Number.isNaN(new Date(image.captured_at).getTime())
      ? new Date(image.captured_at).toISOString()
      : undefined;

  return {
    provider: 'mapillary',
    id: String(image.id),
    position,
    heading: Number(image.computed_compass_angle ?? image.compass_angle) || 0,
    imageUrls: urls,
    attribution: {
      sourceName: 'Mapillary',
      author: image.creator?.username,
      license: 'CC-BY-SA-4.0',
      capturedAt,
      sourceUrl: `https://www.mapillary.com/app/?pKey=${encodeURIComponent(String(image.id))}`,
    },
    meta: { sequence: image.sequence } satisfies MapillaryMeta,
  };
}

/** Extracts 360° pictures from a Mapillary coverage tile. */
export function readPanoramasFromTile(buffer: ArrayBuffer, tile: { x: number; y: number; z: number }): TileImage[] {
  if (buffer.byteLength === 0) return [];
  const vectorTile = new VectorTile(new PbfReader(new Uint8Array(buffer)));
  const layer = vectorTile.layers.image;
  if (!layer) return [];
  const result: TileImage[] = [];
  for (let i = 0; i < layer.length; i++) {
    const feature = layer.feature(i);
    const props = feature.properties;
    if (props.is_pano !== true && props.is_pano !== 'true') continue;
    if (props.id === undefined) continue;
    const geojson = feature.toGeoJSON(tile.x, tile.y, tile.z);
    const position = geojson.geometry.type === 'Point' ? toLatLng(geojson.geometry.coordinates) : null;
    if (!position) continue;
    result.push({
      id: props.id as number | string,
      sequenceId: typeof props.sequence_id === 'string' ? props.sequence_id : undefined,
      position,
    });
  }
  return result;
}

export function createMapillaryProvider(token: string = config.mapillaryAccessToken): PanoramaProvider {
  const sequenceCache = new Map<string, Promise<string[]>>();
  let originalFieldSupported = true;

  const withToken = (url: string) => `${url}${url.includes('?') ? '&' : '?'}access_token=${encodeURIComponent(token)}`;

  function getSequence(sequenceId: string, signal?: AbortSignal): Promise<string[]> {
    let cached = sequenceCache.get(sequenceId);
    if (!cached) {
      cached = fetchJson<{ data?: { id?: string }[] }>(withToken(`${GRAPH_URL}/image_ids?sequence_id=${encodeURIComponent(sequenceId)}`), {
        signal,
      }).then((r) => (r.data ?? []).map((d) => String(d.id)).filter(Boolean));
      cached.catch(() => sequenceCache.delete(sequenceId));
      sequenceCache.set(sequenceId, cached);
    }
    return cached;
  }

  async function fetchImage(id: string, signal?: AbortSignal): Promise<MapillaryImage> {
    const fields = originalFieldSupported ? [...BASE_FIELDS, 'thumb_original_url'] : BASE_FIELDS;
    try {
      return await fetchJson<MapillaryImage>(withToken(`${GRAPH_URL}/${encodeURIComponent(id)}?fields=${fields.join(',')}`), { signal });
    } catch (error) {
      if (originalFieldSupported && error instanceof HttpError && error.status === 400) {
        originalFieldSupported = false;
        return fetchImage(id, signal);
      }
      throw error;
    }
  }

  /** Tile ids can exceed Number.MAX_SAFE_INTEGER: recover the exact string id from the sequence. */
  async function resolveId(image: TileImage, signal: AbortSignal): Promise<string | null> {
    if (typeof image.id === 'string') return image.id;
    if (Number.isSafeInteger(image.id)) return String(image.id);
    if (!image.sequenceId) return null;
    const ids = await getSequence(image.sequenceId, signal);
    let best: string | null = null;
    let bestDiff = Infinity;
    for (const id of ids) {
      const diff = Math.abs(Number(id) - (image.id as number));
      if (diff < bestDiff) {
        bestDiff = diff;
        best = id;
      }
    }
    return best;
  }

  async function loadPanorama(id: string, signal?: AbortSignal): Promise<Panorama> {
    const panorama = parseMapillaryImage(await fetchImage(id, signal), prefersLightImages());
    if (!panorama) throw new Error('Cette image n’est pas un panorama 360°.');
    return panorama;
  }

  return {
    id: 'mapillary',
    name: 'Mapillary',
    description: 'Images participatives du monde entier (licence CC-BY-SA). Jeton d’accès gratuit requis.',
    viewer: 'sphere',
    mapKind: 'leaflet',
    envVar: 'VITE_MAPILLARY_ACCESS_TOKEN',
    isConfigured: () => token.length > 0,
    supportedMaps: ['world', 'europe', 'france', 'americas', 'asia-oceania', 'africa'],

    async findRandom(map: GameMap, { signal, exclude, rng = Math.random }: FindOptions): Promise<Panorama> {
      if (!token) throw new ProviderUnavailableError('mapillary', 'Aucun jeton Mapillary configuré (VITE_MAPILLARY_ACCESS_TOKEN).');
      const minDistanceKm = map.scaleKm / 15;
      const light = prefersLightImages();
      const result = await batchSearch({
        signal,
        batches: 5,
        parallel: 3,
        attempt: async (attemptSignal) => {
          const place = pickPlace(map, 'mapillary', rng);
          const tile = lngLatToTile(randomPointNear(place, JITTER_KM, rng), TILE_ZOOM);
          let buffer: ArrayBuffer;
          try {
            buffer = await fetchArrayBuffer(withToken(`${TILE_URL}/${tile.z}/${tile.x}/${tile.y}`), { signal: attemptSignal });
          } catch (error) {
            if (error instanceof HttpError && error.status === 404) return null;
            throw error;
          }
          const candidates = readPanoramasFromTile(buffer, tile).filter((c) => isFarEnough(c.position, exclude, minDistanceKm));
          // Try a few random candidates of the tile.
          for (let tries = 0; tries < 3 && candidates.length > 0; tries++) {
            const [candidate] = candidates.splice(Math.floor(rng() * candidates.length), 1);
            const id = await resolveId(candidate, attemptSignal);
            if (!id) continue;
            const panorama = parseMapillaryImage(await fetchImage(id, attemptSignal), light);
            if (panorama && isFarEnough(panorama.position, exclude, minDistanceKm)) return panorama;
          }
          return null;
        },
      });
      if (result.panorama) return result.panorama;
      const authError = result.errors.find((e) => e instanceof HttpError && e.isAuthError);
      if (authError || allAttemptsFailed(result)) {
        const error = authError ?? result.errors[0];
        throw new ProviderUnavailableError('mapillary', `Mapillary est indisponible : ${describeError(error)}.`, error);
      }
      throw new NoPanoramaFoundError('mapillary', 'Aucun panorama 360° Mapillary trouvé dans les zones explorées.');
    },

    async findNearby(target, { radiusKm, excludeId, signal }) {
      // Small boxes only: the Graph API rejects large bbox searches.
      const bbox = bboxAround(target, radiusKm).join(',');
      const data = await fetchJson<{ data?: MapillaryImage[] }>(
        withToken(`${GRAPH_URL}/images?bbox=${bbox}&limit=50&fields=${BASE_FIELDS.join(',')}`),
        { signal },
      );
      const panoramas = (data.data ?? [])
        .map((img) => parseMapillaryImage(img, prefersLightImages()))
        .filter((p): p is Panorama => p !== null);
      return nearestPanorama(target, panoramas, excludeId);
    },

    async getNeighbors(panorama: Panorama, signal?: AbortSignal): Promise<NeighborLink[]> {
      const sequenceId = (panorama.meta as MapillaryMeta | undefined)?.sequence;
      if (!sequenceId) return [];
      const ids = await getSequence(sequenceId, signal);
      const index = ids.indexOf(panorama.id);
      if (index < 0) return [];
      const neighborIds = [ids[index + 1], ids[index - 1]].filter((id): id is string => Boolean(id));
      if (neighborIds.length === 0) return [];
      const data = await fetchJson<{ data?: MapillaryImage[] }>(
        withToken(`${GRAPH_URL}/images?image_ids=${neighborIds.join(',')}&fields=id,geometry,computed_geometry`),
        { signal },
      );
      return (data.data ?? []).flatMap((img) => {
        const position = toLatLng(img.computed_geometry?.coordinates) ?? toLatLng(img.geometry?.coordinates);
        if (!img.id || !position) return [];
        const id = String(img.id);
        return [{ id, position, load: (s?: AbortSignal) => loadPanorama(id, s) }];
      });
    },
  };
}
